/**
 * When to remind the user to export a backup (requirements §3.10: "no sync: periodic export
 * reminder"). The reminder never blocks anything; it only shows a hint.
 */

export const MS_PER_DAY = 86_400_000;
/** Remind when the last export is older than this many days. */
export const REMINDER_AFTER_EXPORT_DAYS = 14;
/** Remind a user who never exported once the app has been in use this many days. */
export const REMINDER_AFTER_FIRST_USE_DAYS = 7;
/** After "Later", stay quiet this many days. */
export const REMINDER_SNOOZE_DAYS = 7;

export const ReminderKind = {
  /** The last export is older than `REMINDER_AFTER_EXPORT_DAYS`. */
  Stale: 'stale',
  /** There has never been an export, and the app is in use for `REMINDER_AFTER_FIRST_USE_DAYS` days. */
  Never: 'never',
} as const;
export type ReminderKind = (typeof ReminderKind)[keyof typeof ReminderKind];

export interface ReminderInput {
  readonly nowMs: number;
  readonly syncEnabled: boolean;
  /** Epoch ms of the last full export, null when there was none. */
  readonly lastExportAt: number | null;
  /** Epoch ms when the app was first used on this device, null when unknown. */
  readonly firstUseAt: number | null;
  /** Epoch ms when the user chose "Later", null when never. */
  readonly dismissedAt: number | null;
}

export interface ExportReminder {
  readonly kind: ReminderKind;
  /** Whole days since the last export (Stale) or since first use (Never). */
  readonly days: number;
}

const wholeDays = (fromMs: number, toMs: number): number => Math.floor((toMs - fromMs) / MS_PER_DAY);

/** The reminder to show now, or null. Sync keeps a second copy of the data, so it silences the reminder. */
export function exportReminderOf(input: ReminderInput): ExportReminder | null {
  if (input.syncEnabled) return null;
  if (input.dismissedAt !== null && input.nowMs - input.dismissedAt < REMINDER_SNOOZE_DAYS * MS_PER_DAY)
    return null;
  if (input.lastExportAt !== null) {
    return input.nowMs - input.lastExportAt > REMINDER_AFTER_EXPORT_DAYS * MS_PER_DAY
      ? { kind: ReminderKind.Stale, days: wholeDays(input.lastExportAt, input.nowMs) }
      : null;
  }
  if (input.firstUseAt === null) return null;
  const days = wholeDays(input.firstUseAt, input.nowMs);
  return days >= REMINDER_AFTER_FIRST_USE_DAYS ? { kind: ReminderKind.Never, days } : null;
}

/** Epoch ms of a stored timestamp: a number as text, or an ISO 8601 date; null when absent or unreadable. */
export function parseStoredTime(value: string | null): number | null {
  if (value === null || value.trim() === '') return null;
  const numeric = Number(value);
  const parsed = Number.isFinite(numeric) ? numeric : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
